import type { ReactNode, HTMLAttributes, MouseEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../basic/Button/Button';
import styles from './Modal.module.css';
import { Alert } from '@chikara/ui';
import { AlertProps } from '@chikara/ui';

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Modal description - can be string or ReactNode */
  description?: string | ReactNode;
  /** Optional custom body content (e.g. form). When set, description can be omitted. */
  aditionalDescription?: string | ReactNode;
  /** Optional custom body content (e.g. form). When set, description can be omitted. */
  children?: ReactNode;
  /** Optional icon to display at the top */
  icon?: ReactNode;
  /** Action button label */
  actionLabel?: string;
  /** Callback when action button is clicked */
  onAction?: () => void;
  /** Whether clicking the overlay closes the modal */
  closeOnOverlayClick?: boolean;
  /** Whether pressing ESC closes the modal */
  closeOnEsc?: boolean;
  /** Whether to show the close button (X) */
  showCloseButton?: boolean;
  /** Loading state for action button */
  isActionLoading?: boolean;
  /** Disabled state for action button */
  isActionDisabled?: boolean;
  /** Optional secondary action label */
  secondaryActionLabel?: string;
  /** Callback for secondary action */
  onSecondaryAction?: () => void;
  /** Container element for the portal. Use null to render inline (useful for Storybook) */
  portalContainer?: Element | null;
  /** Whether to automatically focus the modal when opened */
  autoFocus?: boolean;
  /** Alert */
  alert?: AlertProps;
  /** Content width: sm 360px, md 400px, lg 480px (for forms/custom content) */
  contentSize?: 'sm' | 'md' | 'lg';
  /** Secondary action as link (red text, no background) or button */
  secondaryActionVariant?: 'button' | 'link';
  /** Optional class for the title (e.g. to increase size) */
  titleClassName?: string;
}

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  aditionalDescription,
  children,
  icon,
  actionLabel,
  onAction,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  showCloseButton = true,
  isActionLoading = false,
  isActionDisabled = false,
  secondaryActionLabel,
  onSecondaryAction,
  portalContainer,
  autoFocus = true,
  className,
  alert,
  contentSize = 'md',
  secondaryActionVariant = 'button',
  titleClassName,
  ...props
}: ModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2, 9)}`);
  const descriptionId = useRef(`modal-desc-${Math.random().toString(36).slice(2, 9)}`);
  const bodyContentId = useRef(`modal-body-${Math.random().toString(36).slice(2, 9)}`);

  // Handle open/close animations
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to trigger CSS animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 100); // Match transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    // Don't lock scroll if rendering inline (e.g. for Storybook)
    if (isOpen && portalContainer !== null) {
      const body = document.body;
      const openCount = parseInt(body.getAttribute('data-modal-open-count') || '0', 10);
      
      // If this is the first modal, save the original overflow
      if (openCount === 0) {
        body.setAttribute('data-original-overflow', body.style.overflow);
        body.style.overflow = 'hidden';
      }
      
      // Increment the count
      body.setAttribute('data-modal-open-count', (openCount + 1).toString());

      return () => {
        const currentCount = parseInt(body.getAttribute('data-modal-open-count') || '1', 10);
        const newCount = Math.max(0, currentCount - 1);
        body.setAttribute('data-modal-open-count', newCount.toString());
        
        // If this was the last modal, restore the original overflow
        if (newCount === 0) {
          body.style.overflow = body.getAttribute('data-original-overflow') || '';
          body.removeAttribute('data-original-overflow');
        }
      };
    }
  }, [isOpen, portalContainer]);

  // Store and restore focus
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      
      if (autoFocus) {
        // Focus the modal container after animation starts
        const timer = setTimeout(() => {
          modalRef.current?.focus();
        }, 50);
        return () => clearTimeout(timer);
      }
    } else if (previousActiveElement.current) {
      previousActiveElement.current.focus();
      previousActiveElement.current = null;
    }
  }, [isOpen, autoFocus]);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEsc, onClose]);

  // Focus trap
  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Tab') return;

      const modal = modalRef.current;
      if (!modal) return;

      const focusableElements = modal.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!firstElement || !lastElement) return;

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    },
    []
  );

  // Handle overlay click
  const handleOverlayClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (closeOnOverlayClick && event.target === event.currentTarget) {
        onClose();
      }
    },
    [closeOnOverlayClick, onClose]
  );

  // Handle close button click
  const handleCloseClick = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!shouldRender) return null;

  const overlayClasses = [
    styles.overlay,
    isAnimating ? styles.open : '',
    portalContainer === null ? styles.inline : '',
  ]
    .filter(Boolean)
    .join(' ');

  const contentClasses = [
    styles.content,
    styles[contentSize],
    isAnimating ? styles.open : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const modalContent = (
    <div
      className={overlayClasses}
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className={contentClasses}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId.current}
        aria-describedby={
          [
            (description != null && description !== '') && descriptionId.current,
            children && bodyContentId.current,
          ]
            .filter(Boolean)
            .join(' ') || undefined
        }
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {showCloseButton && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleCloseClick}
            aria-label="Cerrar modal"
          >
            <CloseIcon />
          </button>
        )}

        {icon && (
          <div className={styles.iconContainer} aria-hidden="true">
            {icon}
          </div>
        )}

        <div className={styles.textContainer}>
          <h2
            id={titleId.current}
            className={[styles.title, titleClassName].filter(Boolean).join(' ')}
          >
            {title}
          </h2>
          {(description != null && description !== '') && (
            <div id={descriptionId.current} className={styles.description}>
              {description}
            </div>
          )}
          {(aditionalDescription != null && aditionalDescription !== '') && (
            <div id={descriptionId.current} className={styles.description + ' ' + styles.warning}>
              {aditionalDescription}
            </div>
          )}
        </div>

        {children && (
          <div id={bodyContentId.current} className={styles.bodyContent}>
            {children}
          </div>
        )}

        {actionLabel && onAction && <div className={styles.actions}>
          <Button
            variant="primary"
            onClick={onAction}
            isLoading={isActionLoading}
            disabled={isActionDisabled}
            fullWidth
          >
            {actionLabel}
          </Button>
          {secondaryActionLabel && onSecondaryAction && (
            secondaryActionVariant === 'link' ? (
              <div className={styles.actionsSecondaryLinkWrap}>
                <Button
                  variant="link-tertiary"
                  onClick={onSecondaryAction}
                >
                  {secondaryActionLabel}
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                onClick={onSecondaryAction}
                fullWidth
              >
                {secondaryActionLabel}
              </Button>
            )
          )}
          {alert && <Alert variant={alert.variant} icon={alert.icon} className={styles.className}>{alert.children}</Alert>}
        </div>
        }

      </div>
    </div>
  );

  // If portalContainer is null, render inline (useful for Storybook)
  if (portalContainer === null) {
    return modalContent;
  }

  // Use provided container or default to document.body
  return createPortal(modalContent, portalContainer ?? document.body);
}

Modal.displayName = 'Modal';

// Internal close icon component
function CloseIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M1 1L13 13M13 1L1 13"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

